/** Public klien-facing share page (no auth — uses share token). */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import ky from "ky";

const publicApi = ky.create({
  prefixUrl: import.meta.env.VITE_API_BASE ?? "/",
  timeout: 30_000,
  retry: 0,
});

interface ValidateResponse {
  klien_full_name: string;
  parent_phone_masked: string;
  otp_required: boolean;
  expires_at: string;
}

interface VerifyResponse {
  klien_full_name: string;
  package_tier: string;
  report_html: string;
  report_pdf_url?: string;
  rotated_token: string;
  next_expires_at: string;
}

export function SharePage() {
  // wouter's useLocation() returns the pathname ONLY (no query string) — the
  // old `location.split("?")[1]` was therefore always undefined, so ?token=...
  // never reached this component and every visitor saw "Token tidak ditemukan
  // di URL." Read the query string from window.location.search instead.
  useLocation();
  const params = new URLSearchParams(window.location.search);
  const initialToken = params.get("token") ?? "";

  const [token, setToken] = useState(initialToken);
  const [info, setInfo] = useState<ValidateResponse | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [report, setReport] = useState<VerifyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await publicApi.get(`api/public/share/validate?token=${encodeURIComponent(token)}`).json<ValidateResponse>();
        setInfo(r);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Token invalid");
      }
    })();
  }, [token]);

  async function sendOtp() {
    setLoading(true);
    setError(null);
    try {
      await publicApi.post("api/public/share/otp/send", { json: { token } });
      setOtpSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function verifyOtp() {
    setLoading(true);
    setError(null);
    try {
      const r = await publicApi
        .post("api/public/share/otp/verify", { json: { token, otp_code: otpCode } })
        .json<VerifyResponse>();
      setReport(r);
      setToken(r.rotated_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "OTP invalid");
    } finally {
      setLoading(false);
    }
  }

  if (report) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-6">
        <div className="max-w-3xl mx-auto card">
          <h1 className="text-2xl font-semibold mb-4">Laporan DFA — {report.klien_full_name}</h1>
          <div dangerouslySetInnerHTML={{ __html: report.report_html }} />
          {report.report_pdf_url && (
            <a href={report.report_pdf_url} download className="btn-primary inline-block mt-4">
              Download PDF
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="card max-w-md text-center">
          <p className="text-slate-600">Token tidak ditemukan di URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="card w-full max-w-md">
        <h1 className="text-xl font-semibold mb-2">Laporan DFA</h1>
        {info ? (
          <>
            <p className="text-sm text-slate-600 mb-4">
              Halo, untuk membuka laporan <strong>{info.klien_full_name}</strong> kami akan kirim kode OTP ke nomor {info.parent_phone_masked}.
            </p>
            {!otpSent ? (
              <button type="button" className="btn-primary w-full" disabled={loading} onClick={sendOtp}>
                {loading ? "Mengirim…" : "Kirim OTP"}
              </button>
            ) : (
              <>
                <label className="block text-xs font-medium text-slate-700 mb-1">Kode OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  className="input mb-3"
                  placeholder="6 digit"
                  maxLength={6}
                />
                <button type="button" className="btn-primary w-full" disabled={loading || otpCode.length < 4} onClick={verifyOtp}>
                  {loading ? "Memverifikasi…" : "Buka laporan"}
                </button>
              </>
            )}
            {error && <div className="text-sm text-tabrakan mt-3">{error}</div>}
          </>
        ) : error ? (
          <div className="text-sm text-tabrakan">{error}</div>
        ) : (
          <div className="text-sm text-slate-500">Memuat…</div>
        )}
      </div>
    </div>
  );
}
